import React, { useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useGetJobMetricSeriesQuery } from "~/shared/api/analytics/analytics.grpc";
import { useListServiceReceiversQuery } from "~/shared/api/organization/organization.grpc";
import { DateValue } from "~/shared/lib/analytics-utils";
import { AnalyticsLineChart } from "./AnalyticsLineChart";
import { ChartEntitySelect } from "./ChartEntitySelect";

interface Props {
  startDate: DateValue;
  endDate: DateValue;
  jobCategory: ConnectProto.JobCategory;
}

export const CustomerActivityChart: React.FC<Props> = ({ startDate, endDate, jobCategory }) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const [selectedClient, setSelectedClient] = useState<string>("all");

  const { data: receiversData } = useListServiceReceiversQuery(
    { organizationId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined },
    { skip: !selectedCompanyId },
  );

  const clients = useMemo(
    () =>
      (receiversData?.organizations ?? []).map((o) => ({
        id: String(o.id),
        name: o.name ?? String(o.id),
      })),
    [receiversData],
  );

  const { data, isLoading, isFetching } = useGetJobMetricSeriesQuery(
    {
      ownerOrganizationId: BigInt(selectedCompanyId || 0),
      period: { from: startDate.toString(), to: endDate.toString() },
      metric: ConnectProto.JobMetric.ORDERED,
      jobCategory,
      serviceReceiverId: selectedClient !== "all" ? BigInt(selectedClient) : undefined,
    },
    { skip: !selectedCompanyId },
  );

  return (
    <div className="h-full flex flex-col border rounded-xl bg-white p-4">
      <div className="mb-2">
        <h3 className="text-lg text-gray-900 font-bold">
          {t(Namespace.Analytics, "charts.customers.title")}
        </h3>
        <div className="mb-1 mt-3 flex items-center justify-between">
          <p className="text-sm font-semibold">
            {t(Namespace.Analytics, "charts.customers.subtitle")}:{" "}
            <span className="text-blue-600">{String(Number(data?.total ?? 0n))}</span>
          </p>
          <ChartEntitySelect
            value={selectedClient}
            options={clients}
            allLabel={t(Namespace.Analytics, "charts.customers.allClients")}
            onChange={setSelectedClient}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <AnalyticsLineChart
          series={[
            {
              label: t(Namespace.Analytics, "charts.customers.orders"),
              perDay: (data?.perDay ?? []).map((p) => ({
                date: p.date ?? "",
                value: Number(p.value ?? 0n),
              })),
            },
          ]}
          startDate={startDate}
          endDate={endDate}
          isLoading={isLoading || isFetching}
          valueSuffix={t(Namespace.Analytics, "charts.customers.orders")}
          total={`${Number(data?.total ?? 0n)} ${t(Namespace.Analytics, "charts.customers.orders")}`}
        />
      </div>
    </div>
  );
};
